import React, { useEffect, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../../APIs";
import { myLocalStorage } from "../../../../components/StorageHelper";
import { GrServerCluster } from "react-icons/gr";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import SecurityIcon from "@mui/icons-material/Security";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TailwindSwitch from "../../../../tailwind-ui/components/TailwindSwitch";

const BLUE = "#3b83f6";

const Sk = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 dark:bg-gray-700/60 ${className}`} />
);

const ClustersListSkeleton = () => (
  <div className="flex flex-col w-full gap-4 font-sans px-6">
    <Sk className="h-6 w-32 rounded" />
    <Sk className="h-10 w-full max-w-md rounded-xl" />
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {Array.from({ length: 6 }).map((_, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between px-4 py-4 border-b border-gray-100 dark:border-gray-800"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Sk className="h-10 w-10 rounded-full flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <Sk className="h-4 w-1/3 rounded" />
              <Sk className="h-3 w-1/4 rounded mt-2" />
            </div>
          </div>
          <Sk className="h-4 w-24 rounded" />
          <Sk className="h-4 w-24 rounded ml-4" />
        </div>
      ))}
    </div>
  </div>
);

const RolesListSkeleton = () => (
  <div className="flex flex-col w-full gap-4 font-sans px-6">
    <div className="flex items-center gap-3">
      <Sk className="h-10 w-10 rounded-full" />
      <Sk className="h-6 w-48 rounded" />
    </div>

    <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
      <Sk className="h-9 w-16 rounded-lg mx-1" />
      <Sk className="h-9 w-20 rounded-lg mx-1" />
      <Sk className="h-9 w-24 rounded-lg mx-1" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="text-white text-center py-3.5 font-semibold tracking-wide" style={{ backgroundColor: BLUE }}>
          Publish Roles
        </div>

        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
          <Sk className="h-10 w-full rounded-xl" />
        </div>

        <div className="flex-1 overflow-y-auto">
          {Array.from({ length: 10 }).map((_, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Sk className="h-9 w-9 rounded-full flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <Sk className="h-4 w-2/3 rounded" />
                  <Sk className="h-3 w-1/3 rounded mt-2" />
                </div>
              </div>
              <Sk className="h-6 w-12 rounded-full flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Sk className="h-10 w-10 rounded-full" />
            <div className="min-w-0 flex-1">
              <Sk className="h-5 w-2/3 rounded" />
              <Sk className="h-4 w-1/3 rounded mt-2" />
            </div>
          </div>
        </div>

        <div className="px-5 pt-4">
          <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700 pb-3">
            <Sk className="h-4 w-16 rounded" />
            <Sk className="h-4 w-24 rounded" />
          </div>
        </div>

        <div className="p-5 flex-1 overflow-auto space-y-5">
          {Array.from({ length: 5 }).map((_, idx) => (
            <div key={idx} className="border-t border-gray-100 dark:border-gray-800 pt-5 first:border-t-0 first:pt-0">
              <Sk className="h-3 w-24 rounded" />
              <Sk className="h-4 w-full rounded mt-2" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const K8Roles = ({
  selectedTenantName,
  allapi,
  rolesApi,
  cloud,
  rolespoliciesApi,
}) => {
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [k8Roles, setK8Roles] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleRules, setSelectedRoleRules] = useState([]);
  const [clusterId, setClusterId] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [searchCluster, setSearchCluster] = useState("");
  const [selectedDetailsTab, setSelectedDetailsTab] = useState(0);
  const [isRulesLoading, setIsRulesLoading] = useState(false);

  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;

  const handleClusterSearchChange = (event) => {
    setSearchCluster(event.target.value);
  };

  const fetchAllK8Resources = async () => {
    const response = await axios.get(`${NGROK}${allapi}`);
    if (response.data) {
      let data = response.data;
      if (cloud === "AWS") {
        setClusters(response.data.clusters);
      } else setClusters(response.data);
    }
    setLoading(false);
  };

  const getAllK8Roles = async (filterType, clusterId) => {
    const response = await axios.get(
      `${NGROK}${rolesApi}?clusterId=${clusterId}&tenantName=${latestTenant}&cloud=${cloud}&level=${filterType.toUpperCase()}`
    );
    if (response.status === 200) return response.data;
    else return [];
  };

  const fetchData = async (initialLoad = false, filterType, clusterId) => {
    try {
      const [roles] = await Promise.all([getAllK8Roles(filterType, clusterId)]);
      setK8Roles(roles);
      setLoading(false);
      if (initialLoad && roles.length > 0) {
        setSelectedRole(roles[0]);
        getRules(roles[0]);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    try {
      setSelectedRole(null);
      setSelectedRoleRules([]);
      let clusterId = selectedCluster?.azureId;
      if (cloud === "AWS") clusterId = selectedCluster.clusterArn;
      else if (cloud === "GCP") clusterId = selectedCluster?.gcpClusterId;
      fetchData(true, newFilter, clusterId);
      setFilter(newFilter);
    } catch (e) {
      console.log(e);
    }
  };

  const handleRoleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleSearchSubmit = () => {
    // Search is already reactive via state
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const filteredRoles = k8Roles?.filter(
    (role) =>
      role?.name && role?.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredClusters = clusters.filter((resource) =>
    cloud === "AWS"
      ? resource.clusterName.toLowerCase().includes(searchCluster.toLowerCase())
      : resource.name.toLowerCase().includes(searchCluster.toLowerCase())
  );

  const getRules = async (role) => {
    setIsRulesLoading(true);
    try {
      const response = await axios.get(
        `${NGROK}${rolespoliciesApi}?uid=${role?.uid}&tenantName=${latestTenant}&cloud=${role?.cloudType}&clusterId=${role?.clusterId}`
      );
      if (response.status === 200) {
        if (response?.data) setSelectedRoleRules(response.data);
        else setSelectedRoleRules([]);
      } else setSelectedRoleRules([]);
    } catch (error) {
      setSelectedRoleRules([]);
    }
    setIsRulesLoading(false);
  };

  const handleRoleClick = (role) => {
    setSelectedRole(role);
    setSelectedDetailsTab(0);
    getRules(role);
  };

  const handlePublishToggle = async (event, role, cluster, uid) => {
    console.log(role);
    const published = event.target.checked;
    setK8Roles((prevData) =>
      prevData.map((r) =>
        r.uid === role.uid ? { ...r, published } : r
      )
    );
    let requestData = {
      resourceId: role?.uid,
      wsTenantName: selectedTenantName,
      flag: event.target.checked,
      type: "NAMESPACE_ROLE",
      cloudProviderType: cloud,
      clusterId:
        cloud === "AWS"
          ? selectedCluster.clusterArn
          : cloud === "GCP"
            ? selectedCluster?.gcpClusterId
            : selectedCluster?.azureId,
      resourceAccountId: role.resourceAccountId,
    };

    const response = await axios.patch(
      `${NGROK}/api/k8-resources/v1/publish`,
      requestData
    );
  };

  useEffect(() => {
    fetchAllK8Resources();
  }, [latestTenant]);

  useEffect(() => {
    if (selectedCluster) {
      setLoading(true);
      handleFilterChange("all");
    }
  }, [selectedCluster]);

  const getRoleType = (role) => {
    if (role?.level === "CLUSTER") return "Cluster Role";
    if (role?.level === "NAMESPACE") return "Namespace Role";
    return "Role";
  };

  const selectedKey = selectedRole?.uid;

  const renderRulesContent = () => {
    if (isRulesLoading) {
      return (
        <div className="flex justify-center py-8">
          <div
            className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: BLUE, borderTopColor: "transparent" }}
          />
        </div>
      );
    }

    const rulesArray = Array.isArray(selectedRoleRules) ? selectedRoleRules : [selectedRoleRules];

    if (!selectedRoleRules || rulesArray.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <VpnKeyIcon sx={{ fontSize: 36, color: "rgba(0,0,0,0.25)" }} />
          <p className="text-sm mt-2">No rules available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {rulesArray.map((rule, ruleIndex) => (
          <div key={ruleIndex} className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-3">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
              Rule {ruleIndex + 1}
            </div>

            {rule?.clusterRoleUID && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cluster Role UID
                </span>
                <p className="text-sm text-gray-900 dark:text-white mt-1 break-all">
                  {rule.clusterRoleUID}
                </p>
              </div>
            )}

            {rule?.namespaceRoleUID && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Namespace Role UID
                </span>
                <p className="text-sm text-gray-900 dark:text-white mt-1 break-all">
                  {rule.namespaceRoleUID}
                </p>
              </div>
            )}

            {rule?.verbs && rule.verbs.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Verbs
                </span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {rule.verbs.map((verb, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                    >
                      {verb}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rule?.apiGroups && rule.apiGroups.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  API Groups
                </span>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {rule.apiGroups.join(", ") || "core"}
                </p>
              </div>
            )}

            {rule?.resources && rule.resources.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Resources
                </span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {rule.resources.map((res, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                    >
                      {res}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rule?.nonResourceURLs && rule.nonResourceURLs.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Non Resource URLs
                </span>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {rule.nonResourceURLs.join(", ")}
                </p>
              </div>
            )}

            {rule?.resourceNames && rule.resourceNames.length > 0 && (
              <div>
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Resource Names
                </span>
                <p className="text-sm text-gray-900 dark:text-white mt-1">
                  {rule.resourceNames.join(", ")}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Cluster Selection View
  if (selectedCluster === null) {
    return (
      <div className="flex flex-col w-full gap-4 font-sans px-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Clusters</h3>

        <div className="relative w-full max-w-md">
          <input
            type="text"
            placeholder="Search clusters..."
            value={searchCluster}
            onChange={handleClusterSearchChange}
            className="w-full pl-4 pr-10 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
            style={{ boxShadow: "none" }}
          />
          <SearchIcon
            fontSize="small"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
          {/* Header */}
          <div
            className="text-white text-center py-3.5 font-semibold tracking-wide"
            style={{ backgroundColor: BLUE }}
          >
            Select Cluster
          </div>

          {/* Clusters List */}
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {filteredClusters.length > 0 ? (
              filteredClusters.map((resource, index) => (
                <div
                  key={index}
                  onClick={() => setSelectedCluster(resource)}
                  className="flex items-center justify-between px-4 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                    >
                      <GrServerCluster className="w-5 h-5" style={{ color: BLUE }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {cloud === "AWS" ? resource.clusterName : resource.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                        Region: {resource.regionName || resource.location || resource.region || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                    <span>{resource.resourceGroupName || "-"}</span>
                    <span>{resource.type || "-"}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <GrServerCluster className="w-12 h-12 mb-2" style={{ color: "rgba(0,0,0,0.25)" }} />
                <p className="text-sm">No clusters found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Roles View (after selecting cluster)
  return (
    <>
      {loading ? (
        <RolesListSkeleton />
      ) : (
        <div className="flex flex-col w-full gap-4 font-sans px-6">
          {/* Back Button + Cluster Name */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setSelectedCluster(null);
                setSelectedRole(null);
                setSelectedRoleRules([]);
              }}
              className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
            >
              <ArrowBackIcon fontSize="small" className="text-gray-600 dark:text-gray-400" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {selectedCluster.name || selectedCluster.clusterName}
            </h3>
          </div>

          {/* Filter Tabs */}
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-1 w-fit">
            {[
              { key: "all", label: "All" },
              { key: "Cluster", label: "Cluster" },
              { key: "NameSpace", label: "Namespace" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleFilterChange(tab.key)}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${filter === tab.key
                  ? "text-white shadow-sm"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                style={filter === tab.key ? { backgroundColor: BLUE } : undefined}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Roles List */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              <div
                className="text-white text-center py-3.5 font-semibold tracking-wide"
                style={{ backgroundColor: BLUE }}
              >
                Publish Roles
              </div>

              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={handleRoleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-4 pr-10 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                    style={{ boxShadow: "none" }}
                  />
                  <button
                    onClick={handleSearchSubmit}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Search"
                    type="button"
                  >
                    <SearchIcon fontSize="small" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {filteredRoles?.length > 0 ? (
                  filteredRoles.map((role, index) => {
                    const roleKey = role.uid || index;
                    const isSelected = roleKey === selectedKey;

                    return (
                      <div
                        key={roleKey}
                        onClick={() => handleRoleClick(role)}
                        className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-all duration-150 ${isSelected
                          ? "border-l-4"
                          : "hover:bg-gray-50 dark:hover:bg-gray-800/50 border-l-4 border-l-transparent"
                          }`}
                        style={
                          isSelected
                            ? { backgroundColor: "rgba(59,131,246,0.06)", borderLeftColor: BLUE }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div
                            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                          >
                            <SecurityIcon sx={{ fontSize: 18, color: BLUE }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 dark:text-white break-all">
                              {role.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                              {getRoleType(role)}
                            </p>
                          </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <TailwindSwitch
                            isDisabled={false}
                            action={(e, data) => handlePublishToggle(e, data)}
                            data={role}
                            isChecked={role.published}
                          />
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <SecurityIcon sx={{ fontSize: 44, color: "rgba(0,0,0,0.25)" }} />
                    <p className="text-sm mt-2">No roles found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Details Panel */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm flex flex-col h-[calc(100vh-320px)] max-h-[740px]">
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: "rgba(59,131,246,0.12)" }}
                      >
                        <SecurityIcon sx={{ fontSize: 20, color: BLUE }} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white break-all">
                          {selectedRole?.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {getRoleType(selectedRole)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="px-5 pt-4">
                    <div className="flex gap-6 border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => setSelectedDetailsTab(0)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 0
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 0 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                        Info
                      </button>
                      <button
                        onClick={() => setSelectedDetailsTab(1)}
                        className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-all ${selectedDetailsTab === 1
                          ? "border-b-2"
                          : "text-gray-500 border-transparent hover:text-gray-700 dark:hover:text-gray-300"
                          }`}
                        style={selectedDetailsTab === 1 ? { color: BLUE, borderColor: BLUE } : undefined}
                      >
                        <VpnKeyIcon fontSize="small" />
                        Role Rules
                      </button>
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="p-5 flex-1 overflow-auto">
                    {selectedDetailsTab === 0 && (
                      <div className="space-y-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role Name
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white break-all">
                            {selectedRole?.name || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            UID
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white font-mono break-all">
                            {selectedRole?.uid || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Role Type
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {getRoleType(selectedRole)}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Cluster ID
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white font-mono break-all">
                            {selectedRole?.clusterId || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Cloud Type
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {selectedRole?.cloudType || cloud || "—"}
                          </p>
                        </div>

                        <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Published
                          </label>
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${selectedRole?.published
                              ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                              }`}
                          >
                            {selectedRole?.published ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedDetailsTab === 1 && renderRulesContent()}
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <SecurityIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.25)" }} />
                  <p className="text-sm mt-2">Select a role to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default K8Roles;
